import { useState } from 'react'
import { ChartColumnBig } from 'lucide-react'
import { smoothPath } from '@/lib/chart-path'
import { cn } from '@/lib/utils'
import type { Freshness, QuarterPoint } from '@/lib/system-intelligence'
import { FreshnessNote, PanelError, PanelLoading } from './PanelState'
import { ChartTooltip } from './ChartTooltip'

const W = 320
const H = 200
/** Headroom kept above and below the curve so the peak and trough stay drawn. */
const PAD = 14

/**
 * A round ceiling at or above `peak`, so the axis reads 1k/2k/3k rather than
 * 1259/2518/3777.
 *
 * The axis used to be a fixed 80k, which was fine for invented numbers and wrong
 * for real ones: a platform doing 1,259 deals a quarter would draw four points
 * flat along the bottom. It is derived per response instead — which does mean
 * the curve's height is only comparable within one screenful, hence the ticks
 * being labelled rather than implied.
 */
function ceiling(peak: number): number {
  if (peak <= 0) return 4
  const magnitude = 10 ** Math.floor(Math.log10(peak))
  for (const step of [1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10]) {
    const candidate = step * magnitude
    if (candidate >= peak) return candidate
  }
  return 10 * magnitude
}

/** Contract value at a glance: "$31.1M", "$904k". */
function money(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1000) return `$${Math.round(value / 1000)}k`
  return `$${Math.round(value)}`
}

/** A filled marker on one series at the hovered quarter. */
function Dot({ point, className }: { point?: { x: number; y: number }; className: string }) {
  if (!point) return null
  return (
    <span
      className={cn('pointer-events-none absolute size-2 rounded-full', className)}
      style={{
        left: `${(point.x / W) * 100}%`,
        top: `${(point.y / H) * 100}%`,
        transform: 'translate(-50%, -50%)',
      }}
    />
  )
}

/** 55000 → "55k"; 1259 → "1.3k"; 0 → "0". */
function short(value: number): string {
  if (value === 0) return '0'
  if (Math.abs(value) < 1000) return String(Math.round(value))
  const thousands = value / 1000
  return `${thousands >= 10 ? Math.round(thousands) : Number(thousands.toFixed(1))}k`
}

interface QuarterlyPerformanceProps {
  data: QuarterPoint[]
  /** The API's year, echoed so the roll-up is never undated. */
  year?: number
  freshness?: Freshness
  isLoading?: boolean
  error?: unknown
  onRetry?: () => void
  compact?: boolean
}

/**
 * Deals closed per quarter as a dotted spline, with the cancellations behind it.
 *
 * Dotted rather than solid on purpose: the series is a quarterly roll-up, not a
 * measured line, and the dots keep it from reading as live telemetry next to the
 * timeline chart beside it. Cancellations get their own faint line because the
 * rates are high enough — Aurora's Q1 was 937 sales against 565 cancellations —
 * that a sales curve alone tells less than half the story.
 */
export function QuarterlyPerformance({
  data,
  year,
  freshness,
  isLoading,
  error,
  onRetry,
  compact,
}: QuarterlyPerformanceProps) {
  /** Quarter under the cursor; null when the pointer is off the plot. */
  const [hovered, setHovered] = useState<number | null>(null)

  const peak = Math.max(...data.map((d) => Math.max(d.sales, d.cancelled)), 0)
  const max = ceiling(peak)
  const ticks = [1, 0.75, 0.5, 0.25, 0].map((f) => f * max)

  const project = (pick: (d: QuarterPoint) => number) =>
    data.map((d, i) => ({
      x: PAD + (i / Math.max(data.length - 1, 1)) * (W - 2 * PAD),
      y: H - PAD - (Math.min(pick(d), max) / max) * (H - 2 * PAD),
    }))

  const salesPoints = project((d) => d.sales)
  const cancelledPoints = project((d) => d.cancelled)

  // The quarter the team is measured against is the last one with any sales in
  // it — a part-finished quarter should not take the callout just for being
  // tall, and future quarters come back as zeros.
  const markIndex = data.reduce((best, d, i) => (d.sales > 0 ? i : best), 0)
  const mark = salesPoints[markIndex]

  /**
   * Nearest quarter to the cursor, measured against the container's width
   * rather than the viewBox — `preserveAspectRatio="none"` means the two do not
   * share a scale.
   */
  const trackCursor = (e: React.MouseEvent<HTMLDivElement>) => {
    const { left, width } = e.currentTarget.getBoundingClientRect()
    if (!width || data.length === 0) return
    const index = Math.round(((e.clientX - left) / width) * (data.length - 1))
    setHovered(Math.min(Math.max(index, 0), data.length - 1))
  }

  return (
    <section
      className={cn(
        'border-line bg-surface flex h-full flex-col rounded-lg border border-dashed',
        compact ? 'p-4' : 'p-5',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-fg-muted flex items-center gap-2 font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
          <ChartColumnBig className="size-4" />
          Quarterly Performance
        </h2>
        {/* Echoed, not chosen. The period selector at the top of the page sets
            the year; a second control here could disagree with it. */}
        {year && <span className="text-fg-subtle font-mono text-[11px]">{year}</span>}
      </div>

      {isLoading ? (
        <PanelLoading className="flex-1" />
      ) : error ? (
        <PanelError error={error} onRetry={onRetry} className="flex-1" />
      ) : data.length === 0 ? (
        <PanelLoading label="No quarters" className="flex-1" />
      ) : (
        <>
          <div className="mt-4 flex flex-1 gap-2">
            {/* Y axis is HTML, not SVG text: the plot stretches with the card and
                non-uniform scaling would squash the labels with it. */}
            <div className="text-fg-subtle flex flex-col justify-between py-[6px] font-mono text-[10px]">
              {ticks.map((tick) => (
                <span key={tick}>{short(tick)}</span>
              ))}
            </div>

            {/* `relative` so the peak label can ride the plot's own coordinate
                space (percentages of the viewBox) and stay on the curve at any
                width. `flex-1` here is horizontal — the parent is a row — so it
                stays on in both densities; compact only pins the height. */}
            <div
              className={cn(
                'relative flex-1 cursor-crosshair',
                compact ? 'h-[118px]' : 'min-h-[150px]',
              )}
              onMouseMove={trackCursor}
              onMouseLeave={() => setHovered(null)}
            >
              <svg
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio="none"
                className="h-full w-full"
                fill="none"
                role="img"
                aria-label={`Sales by quarter: ${data
                  .map(
                    (d) =>
                      `${d.quarter} ${d.sales.toLocaleString()} sold, ${d.cancelled.toLocaleString()} cancelled`,
                  )
                  .join('; ')}`}
              >
                <path
                  d={smoothPath(cancelledPoints, PAD, H - PAD)}
                  stroke="var(--color-critical)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeDasharray="1 5"
                  opacity="0.55"
                  vectorEffect="non-scaling-stroke"
                />
                <path
                  d={smoothPath(salesPoints, PAD, H - PAD)}
                  stroke="var(--color-fg)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray="1 5"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>

              {/* Solid dots on the hovered quarter — the series are dotted, so a
                  filled marker is the one thing that reads as "you are here". */}
              {hovered !== null && salesPoints[hovered] && (
                <>
                  <span
                    className="bg-line-bright pointer-events-none absolute inset-y-0 w-px"
                    style={{ left: `${(salesPoints[hovered].x / W) * 100}%` }}
                  />
                  <Dot point={salesPoints[hovered]} className="bg-fg" />
                  <Dot point={cancelledPoints[hovered]} className="bg-critical" />
                  <ChartTooltip
                    label={`${data[hovered].quarter} ${year ?? ''}`.trim()}
                    rows={[
                      { text: `${data[hovered].sales.toLocaleString()} sold`, tone: 'text-fg' },
                      {
                        text: `${data[hovered].cancelled.toLocaleString()} cancelled`,
                        tone: 'text-critical',
                      },
                      { text: `${Math.round(data[hovered].kw).toLocaleString()} kW` },
                      ...(data[hovered].contractValue === null
                        ? []
                        : [{ text: money(data[hovered].contractValue!) }]),
                    ]}
                    left={(salesPoints[hovered].x / W) * 100}
                    align={hovered === 0 ? 'start' : hovered === data.length - 1 ? 'end' : 'center'}
                  />
                </>
              )}

              {mark && hovered === null && (
                <span
                  className="text-primary-bright absolute font-mono text-[11px] font-semibold"
                  style={{
                    left: `${(mark.x / W) * 100}%`,
                    top: `${(mark.y / H) * 100}%`,
                    transform: 'translate(-50%, -150%)',
                  }}
                >
                  {short(data[markIndex].sales)}
                </span>
              )}
            </div>
          </div>

          <div className="text-fg-subtle mt-2 flex justify-between pl-8 font-mono text-[11px]">
            {data.map((d, i) => (
              <span
                key={d.quarter}
                className={cn(i === hovered && 'text-fg font-semibold')}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                {d.quarter}
              </span>
            ))}
          </div>

          <FreshnessNote freshness={freshness} className="justify-center" />
        </>
      )}
    </section>
  )
}
